import { Link } from "react-router-dom";
import { useRef, useState } from "react";
import { Linkedin, Twitter, Facebook, Instagram, Youtube, MessageCircle, MapPin, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Captcha from "@/components/site/Captcha";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/lib/settings";
import { toast } from "sonner";

const SOCIAL_ICONS = {
  linkedin: Linkedin,
  twitter: Twitter,
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
};

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const captchaRef = useRef(null);
  const { settings } = useSiteSettings();

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    if (!showCaptcha) { setShowCaptcha(true); return; }
    const cap = captchaRef.current?.getValues() || {};
    if (!cap.captcha_answer) { toast.error("Please solve the human check"); return; }
    setLoading(true);
    try {
      const res = await api.post("/job-alerts", { email, ...cap });
      toast.success(res.data?.message || "Subscribed");
      setEmail("");
      setShowCaptcha(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Subscription failed");
      captchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const socialItems = [
    { key: "linkedin", label: "LinkedIn", url: settings.social?.linkedin },
    { key: "twitter", label: "Twitter", url: settings.social?.twitter },
    { key: "facebook", label: "Facebook", url: settings.social?.facebook },
    { key: "instagram", label: "Instagram", url: settings.social?.instagram },
    { key: "youtube", label: "YouTube", url: settings.social?.youtube },
  ].filter((x) => x.url);

  // Prefer the Contact Us phone number; fall back to the social.whatsapp override.
  const waDigits = String(settings.contact_phone || "").replace(/\D/g, "")
    || String(settings.social?.whatsapp || "").replace(/\D/g, "");
  const waUrl = waDigits ? `https://wa.me/${waDigits}` : null;
  const officeLine = (settings.offices || []).join(" · ");

  return (
    <footer className="border-t border-slate-200 bg-slate-950 text-slate-300">
      <div className="container-page py-14">
        <div className="grid gap-10 md:grid-cols-12">
          <div className="md:col-span-4">
            <div className="flex items-center gap-4">
              <span className="logo-mark-dark inline-flex h-16 w-16 items-center justify-center md:h-20 md:w-20">
                <img
                  src={settings.logo_url}
                  alt={`${settings.brand_left}${settings.brand_right} logo`}
                  className="h-full w-full rounded-md object-cover"
                />
              </span>
              <div>
                <div className="font-heading text-2xl font-bold text-white">
                  <span className="text-[#3B8FCE]">{settings.brand_left}</span>
                  <span className="text-secondary">{settings.brand_right}</span>
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-slate-400">{settings.tagline}</div>
              </div>
            </div>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-400" data-testid="footer-description">
              {settings.footer_description || "A specialised pharma recruitment partner — bringing the right scientific talent to growing pharma, biotech and CRO organisations across India and select international markets."}
            </p>
            <div className="mt-6 flex items-center gap-3" data-testid="footer-social-list">
              {socialItems.map(({ key, label, url }) => {
                const Icon = SOCIAL_ICONS[key];
                return (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    data-testid={`footer-social-${key}`}
                    className="rounded-md border border-slate-800 p-2 transition-colors hover:border-secondary hover:text-secondary"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                );
              })}
              {waUrl && (
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="WhatsApp"
                  data-testid="footer-social-whatsapp"
                  className="rounded-md border border-slate-800 p-2 transition-colors hover:border-[#25D366] hover:text-[#25D366]"
                >
                  <MessageCircle className="h-4 w-4" />
                </a>
              )}
              {socialItems.length === 0 && !waUrl && (
                <span className="text-xs text-slate-500">Social links not configured yet.</span>
              )}
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="overline mb-4">Company</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="hover:text-white">About</Link></li>
              <li><Link to="/services" className="hover:text-white">Services</Link></li>
              <li><Link to="/testimonials" className="hover:text-white">Testimonials</Link></li>
              <li><Link to="/blog" className="hover:text-white">Career Blog</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="overline mb-4">For You</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/jobs" className="hover:text-white">Current Openings</Link></li>
              <li><Link to="/upload-resume" className="hover:text-white">Upload Resume</Link></li>
              <li><Link to="/resume-health-check" className="hover:text-white">Resume Health Check</Link></li>
              <li><Link to="/feedback" className="hover:text-white">Feedback</Link></li>
              <li><Link to="/contact" className="hover:text-white">Contact Us</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="overline mb-4">Job Alerts</div>
            <p className="mb-3 text-sm text-slate-400">Get pharma openings delivered to your inbox.</p>
            <form onSubmit={subscribe} className="space-y-2" data-testid="footer-alerts-form">
              <div className="flex gap-2">
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  data-testid="footer-alerts-email"
                  className="border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500 focus-visible:ring-secondary"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="footer-alerts-submit"
                  className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
                >
                  {loading ? "..." : showCaptcha ? "Confirm" : "Subscribe"}
                </Button>
              </div>
              {showCaptcha && <Captcha ref={captchaRef} testId="footer-alerts-captcha" className="!bg-slate-900 !border-slate-700" />}
            </form>
            <div className="mt-6 space-y-2 text-sm text-slate-400">
              {settings.contact_email && (
                <div className="flex items-center gap-2" data-testid="footer-contact-email">
                  <Mail className="h-4 w-4 text-secondary" />
                  <a href={`mailto:${settings.contact_email}`} className="hover:text-white">{settings.contact_email}</a>
                </div>
              )}
              {settings.contact_phone && (
                <div className="flex items-center gap-2" data-testid="footer-contact-phone">
                  <Phone className="h-4 w-4 text-secondary" /> {settings.contact_phone}
                </div>
              )}
              {officeLine && (
                <div className="flex items-start gap-2" data-testid="footer-offices">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-secondary" /> {officeLine}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t border-slate-800 pt-6 text-xs text-slate-500 md:flex-row md:items-center">
          <div>© {new Date().getFullYear()} {settings.brand_left}{settings.brand_right}. All rights reserved.</div>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white">Terms of Use</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
